"use client";

import React, { useCallback, useEffect, useState } from "react";
import {
  type FieldValues,
  type Path,
  type PathValue,
  type UseFormReturn,
  useWatch,
} from "react-hook-form";
import { useDebouncedCallback } from "use-debounce";
import InputWithSuffix from "@/components/form/form-input-sufix";
import { SliderExtended } from "@/components/slider-extended";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { FilterRangePreset } from "./filter-range.types";

type FilterRangeProps<T extends FieldValues> = {
  labelStart: string;
  labelEnd: string;
  paramStart: Path<T>;
  paramEnd: Path<T>;
  unit: string;
  maxValue?: number;
  minValue?: number;
  step?: number;
  rangePresets?: FilterRangePreset[];
  form: UseFormReturn<T>;
};

export const FilterRangeForm = <T extends FieldValues>({
  paramStart,
  paramEnd,
  labelStart,
  labelEnd,
  unit,
  maxValue = 120,
  minValue = 0,
  step = 5,
  rangePresets,
  form,
}: FilterRangeProps<T>) => {
  const watchedStartValue = useWatch({
    control: form.control,
    name: paramStart,
  });
  const watchedEndValue = useWatch({
    control: form.control,
    name: paramEnd,
  });
  const [localValues, setLocalValues] = useState<number[]>([
    Number(form.getValues(paramStart)) || minValue,
    Number(form.getValues(paramEnd)) || maxValue,
  ]);

  useEffect(() => {
    setLocalValues([
      Number(watchedStartValue) || minValue,
      Number(watchedEndValue) || maxValue,
    ]);
  }, [maxValue, minValue, watchedEndValue, watchedStartValue]);

  const updateFormValues = useCallback(
    (values: number[]) => {
      if (values.length !== 2) {
        return;
      }

      const [startValue, endValue] = values;

      if (startValue === undefined || endValue === undefined) {
        return;
      }

      form.setValue(
        paramStart,
        startValue.toString() as PathValue<T, Path<T>>,
        {
          shouldDirty: true,
        },
      );
      form.setValue(paramEnd, endValue.toString() as PathValue<T, Path<T>>, {
        shouldDirty: true,
      });
    },
    [form, paramEnd, paramStart],
  );

  const handleValueChange = useCallback(
    (values: number[] = localValues) => {
      updateFormValues(values);
    },
    [localValues, updateFormValues],
  );

  const handleDebounceChange = useDebouncedCallback((values: number[]) => {
    updateFormValues(values);
  }, 500);

  const handleInputChange = useCallback(
    (index: number, value: number) => {
      setLocalValues((prev) => {
        const nextValues = [...prev];
        nextValues[index] = value;
        handleDebounceChange(nextValues);
        return nextValues;
      });
    },
    [handleDebounceChange],
  );

  const handlePresetClick = useCallback(
    (preset: FilterRangePreset) => {
      const nextValues = [preset.min, preset.max];
      setLocalValues(nextValues);
      handleValueChange(nextValues);
    },
    [handleValueChange],
  );

  const handleSliderChange = useCallback((values: number[]) => {
    if (values.length !== 2) {
      return;
    }

    setLocalValues(values);
  }, []);

  return (
    <div className="grid w-full gap-4">
      {rangePresets?.length ? (
        <ol className="mb-2 grid grid-cols-2 gap-2">
          {rangePresets.map((preset) => {
            const isSelected =
              localValues[0] === preset.min && localValues[1] === preset.max;

            return (
              <li key={`${preset.label}-${preset.min}-${preset.max}`}>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  aria-pressed={isSelected}
                  className={cn(
                    "h-12 w-full border-transparent px-2 font-normal text-base",
                    isSelected && "border-primary",
                  )}
                  onClick={() => handlePresetClick(preset)}
                >
                  {preset.label}
                </Button>
              </li>
            );
          })}
        </ol>
      ) : null}
      <SliderExtended
        value={localValues}
        minStepsBetweenThumbs={1}
        max={maxValue}
        min={minValue}
        step={step}
        onValueChange={handleSliderChange}
        onPointerUp={() => handleValueChange()}
        className={cn("w-full")}
        classNameTrack="bg-white h-0.5"
        classNameThumb="bg-brand-green2-300 h-5 w-5 "
      />

      <div className="mt-2 flex flex-wrap gap-4">
        <ol className="flex w-full items-center gap-4">
          {localValues.map((_, index) => (
            <React.Fragment key={index}>
              <li className="flex h-14 flex-1 flex-col items-start rounded-xl bg-white px-4 py-2">
                <span className="text-black/50">
                  {index % 2 === 0 ? labelStart : labelEnd}
                </span>
                <InputWithSuffix
                  id={unit}
                  unit={unit}
                  initialValue={localValues[index]}
                  className="-mx-2 -my-0.5 w-full px-2 py-0.5 text-base leading-5"
                  callback={(value: number) => handleInputChange(index, value)}
                  min={index === 0 ? minValue : localValues[0]}
                  max={index === 1 ? maxValue : localValues[1]}
                  data-pw={`price-filter-${index}`}
                />
              </li>
              {index % 2 === 0 ? (
                <li className="h-0.5 shrink-0 basis-4 bg-gray-300"></li>
              ) : null}
            </React.Fragment>
          ))}
        </ol>
      </div>
    </div>
  );
};
